import { formatInTimeZone } from 'date-fns-tz'
import { parseISO } from 'date-fns'

const MOSCOW = 'Europe/Moscow'

export function formatMoscowDateTime(iso: string): string {
  try {
    return formatInTimeZone(parseISO(iso), MOSCOW, 'dd.MM.yyyy HH:mm')
  } catch {
    return iso
  }
}

/** Терминальные заказы в таймере: только дата (ADMIN_UX v3 п.3) */
export function formatMoscowDateOnly(iso: string): string {
  try {
    return formatInTimeZone(parseISO(iso), MOSCOW, 'dd.MM.yyyy')
  } catch {
    return iso
  }
}

export function formatMoscowTime(iso: string): string {
  try {
    return formatInTimeZone(parseISO(iso), MOSCOW, 'HH:mm:ss')
  } catch {
    return iso
  }
}

export function formatRub(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(n)
}

export function formatKm(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${new Intl.NumberFormat('ru-RU').format(n)} км`
}

/** Просрочка: SEARCH — от created_at; иначе от status_updated_at; порог > 1 ч */
export function isOverdue(
  status: string,
  createdAt: string,
  statusUpdatedAt: string,
  now: Date = new Date(),
): boolean {
  const base =
    status === 'SEARCH' ? parseISO(createdAt) : parseISO(statusUpdatedAt)
  return now.getTime() - base.getTime() > 60 * 60 * 1000
}

export function elapsedInStatusMs(
  status: string,
  createdAt: string,
  statusUpdatedAt: string,
  now: Date = new Date(),
): number {
  const base =
    status === 'SEARCH' ? parseISO(createdAt) : parseISO(statusUpdatedAt)
  return Math.max(0, now.getTime() - base.getTime())
}

/** SEARCH: до часа ММ:СС, затем Ч:ММ:СС (без ведущего 00:) */
export function formatElapsedSearch(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const sec = totalSec % 60
  const totalMin = Math.floor(totalSec / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) {
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** Активные статусы кроме SEARCH: до часа показываем ММ:СС, затем ЧЧ:ММ */
export function formatElapsedActiveHoursMinutes(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const sec = totalSec % 60
  const totalMin = Math.floor(totalSec / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  // Для коротких интервалов показываем минуты и секунды без избыточных часов.
  if (h === 0) {
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * Таймер на канбане лидов. Прогрессивный, с явными единицами (понятнее, чем
 * «03:13» у заказов):
 *   < 1 ч  → «Мм Сс»      (напр. «23м 14с»)
 *   < 1 сут → «Чч Мм»     (напр. «3ч 05м»)
 *   ≥ 1 сут → «Дд Чч Мм»  (напр. «2д 4ч 30м»)
 */
export function formatElapsedProgressive(ms: number): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const totalSec = Math.floor(Math.max(0, ms) / 1000)
  const sec = totalSec % 60
  const totalMin = Math.floor(totalSec / 60)
  const min = totalMin % 60
  const totalHours = Math.floor(totalMin / 60)
  const hours = totalHours % 24
  const days = Math.floor(totalHours / 24)
  if (totalHours < 1) return `${min}м ${p(sec)}с`
  if (days < 1) return `${hours}ч ${p(min)}м`
  return `${days}д ${hours}ч ${p(min)}м`
}

/** Пороги подсветки таймера лида (янтарь) по статусу, мс. Какие статусы
 *  вообще с таймером — `LEAD_PIPELINE_UI[pipeline].timerStatuses`; у стадии
 *  без своего порога (oo_review) берётся порог in_progress. */
export const LEAD_OVERDUE_THRESHOLDS_MS: Record<string, number> = {
  new: 15 * 60 * 1000,
  in_progress: 2 * 60 * 60 * 1000,
  awaiting_payment: 24 * 60 * 60 * 1000,
}

export function leadOverdueThresholdMs(thresholds: Record<string, number>, status: string): number | undefined {
  return thresholds[status] ?? (status === 'oo_review' ? thresholds.in_progress : undefined)
}

/** @deprecated используйте formatElapsedSearch / formatElapsedActiveHoursMinutes */
export function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  if (m >= 60) return `${m}:${String(sec).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** ISO → значение <input type="datetime-local"> в часовом поясе браузера. */
export function toDateTimeLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
